import { QUALITIES, type Quality } from '../../domain/types'

/** Quantity stepper. Holds a whole number of at least `min`. */
export function Stepper({
  value,
  onChange,
  min = 1,
  ariaLabel,
}: {
  value: number
  onChange: (value: number) => void
  min?: number
  ariaLabel: string
}) {
  const clamp = (next: number) => onChange(Math.max(min, Math.trunc(next) || min))
  return (
    <div className="flex items-stretch rounded border border-edge bg-ink-lift">
      <button
        type="button"
        aria-label={`${ariaLabel}: decrease`}
        className="px-2 text-dim hover:bg-ink-high disabled:opacity-25"
        disabled={value <= min}
        onClick={() => clamp(value - 1)}
      >
        −
      </button>
      <input
        type="number"
        aria-label={ariaLabel}
        className="w-14 border-x border-edge px-1 py-1 text-center text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-gold"
        value={value}
        min={min}
        onChange={(event) => clamp(Number(event.target.value))}
      />
      <button
        type="button"
        aria-label={`${ariaLabel}: increase`}
        className="px-2 text-dim hover:bg-ink-high"
        onClick={() => clamp(value + 1)}
      >
        +
      </button>
    </div>
  )
}

export function QualitySelect({
  value,
  onChange,
}: {
  value: Quality
  onChange: (value: Quality) => void
}) {
  return (
    <select
      aria-label="Quality"
      className="rounded border border-edge bg-ink-lift px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
      value={value}
      onChange={(event) => onChange(event.target.value as Quality)}
    >
      {QUALITIES.map((quality) => (
        <option key={quality} value={quality}>
          {quality}
        </option>
      ))}
    </select>
  )
}

export function NumberField({
  value,
  onChange,
  label,
  min = 0,
}: {
  value: number
  onChange: (value: number) => void
  label: string
  min?: number
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-dim">{label}</span>
      <input
        type="number"
        className="w-full rounded border border-edge px-2 py-1 text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-gold"
        value={value}
        min={min}
        onChange={(event) => onChange(Math.max(min, Math.trunc(Number(event.target.value)) || 0))}
      />
    </label>
  )
}

export function TextField({
  value,
  onChange,
  label,
  multiline = false,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  label: string
  multiline?: boolean
  placeholder?: string
}) {
  const shared =
    'w-full rounded border border-edge px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gold'
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-dim">{label}</span>
      {multiline ? (
        <textarea
          className={`${shared} min-h-[5rem] resize-y leading-relaxed`}
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          type="text"
          className={shared}
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </label>
  )
}

export function RowShell({
  children,
  onRemove,
}: {
  children: React.ReactNode
  onRemove: () => void
}) {
  return (
    <div className="flex items-start gap-2 rounded border border-edge bg-ink-deep p-2">
      <div className="flex flex-1 flex-wrap items-center gap-2">{children}</div>
      <button
        type="button"
        aria-label="Remove row"
        className="rounded px-2 py-1 text-dim hover:bg-danger-bg hover:text-danger"
        onClick={onRemove}
      >
        ✕
      </button>
    </div>
  )
}

export function AddButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="self-start rounded border border-dashed border-edge-lit px-3 py-1 text-sm text-dim hover:border-gold hover:text-gold"
    >
      + {children}
    </button>
  )
}

export function Section({
  title,
  hint,
  children,
  right,
}: {
  title: string
  hint?: string
  children: React.ReactNode
  right?: React.ReactNode
}) {
  return (
    <section className="flex flex-col gap-2 border-t border-edge pt-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-gold">{title}</h3>
        {right}
      </div>
      {hint && <p className="-mt-1 text-xs text-dim">{hint}</p>}
      {children}
    </section>
  )
}
